import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { legalDocumentsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { buildLegalDocumentIframeSrcDoc } from './legal-document-rendering';
import type { GetLegalDocumentResponse } from '@pillar-portal/api-types';

export function LegalDocumentDetailPage() {
    const log = createComponentLogger('LegalDocumentDetailPage');
    const navigate = useNavigate();
    const params = useParams();
    const legalDocumentUuidParam: string | undefined = params.id;
    const legalDocumentUuid: string = useMemo(() => {
        if (legalDocumentUuidParam === undefined) {
            throw new Error('Missing legal document UUID');
        }
        const parseResult = z.string().uuid().safeParse(legalDocumentUuidParam);
        if (!parseResult.success) {
            throw new Error('Invalid legal document UUID');
        }
        return parseResult.data;
    }, [legalDocumentUuidParam]);

    const { data, isLoading, error } = useQuery<GetLegalDocumentResponse>({
        queryKey: ['legalDocument', legalDocumentUuid],
        queryFn: async () => {
            return await legalDocumentsApi.getLegalDocument(legalDocumentUuid);
        },
        staleTime: 0,
    });

    if (isLoading) {
        return (
            <div className="p-6">
                <p>Loading...</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-6">
                <p>Error loading legal document.</p>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="p-6">
                <p>Missing legal document data.</p>
            </div>
        );
    }

    const doc = data.legal_document;
    const srcDoc: string = buildLegalDocumentIframeSrcDoc({
        legalDocumentUuid: doc.legal_document_uuid,
        legalDocumentContentType: doc.legal_document_content_type,
        legalDocumentText: doc.legal_document_text,
    });

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-2xl font-bold">{doc.legal_document_title}</h1>
                    <p className="text-sm text-gray-600">
                        Version: {doc.legal_document_version}
                    </p>
                    <p className="text-sm text-gray-600">
                        In force: {new Date(doc.in_force_since).toLocaleString()} -{' '}
                        {doc.in_force_to === null
                            ? 'No end date'
                            : new Date(doc.in_force_to).toLocaleString()}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => {
                        runInBackground(navigate('/legal-documents'), (err: unknown) => {
                            log.error('Navigation failed', err);
                        });
                    }}
                >
                    Back
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Document</CardTitle>
                </CardHeader>
                <CardContent>
                    <iframe
                        title="Legal Document"
                        sandbox="allow-scripts"
                        className="w-full h-[70vh] border rounded"
                        srcDoc={srcDoc}
                    />
                </CardContent>
            </Card>
        </div>
    );
}
